import type { Speaker } from '@digital-twins/shared-types'
import { getAllQuestions } from '@/lib/utils/chart-utils'

export interface RespondentProfile {
  respondent_id: string
  /** Persona token for opening a chat with this twin, when the study row carries one. */
  chatPersonaToken?: string | null
  name: string
  image_id: string
  image_url: string
  summary: string
  /** The twin's own tagline: never shown as the report's label (D16). */
  tagline?: string
  /** Who this person is in the report, and its label (`speakers`, D16). */
  speaker?: Speaker | null
  respondent_attributes: Record<string, unknown>
  survey_data: Record<string, unknown>
}

export interface SurveyQuestionContext {
  name: string
  title: string
  type?: string
}

export function buildSurveyQuestionContext(
  survey: unknown,
): Array<SurveyQuestionContext> {
  if (!survey) return []
  const questions = getAllQuestions(survey as any)
  return questions.map((q: any) => ({
    name: q.name,
    title: typeof q.title === 'string' ? q.title : (q.title?.default ?? q.name),
    type: q.type,
  }))
}

function getStringField(
  value: unknown,
  keys: Array<string>,
): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  for (const key of keys) {
    const raw = record[key]
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
  }
  return undefined
}

function getResponseImage(
  response: any,
): { image_id?: string; image_url?: string } | null {
  const attrs = response?.respondent_attributes || {}
  const imageUrl =
    getStringField(response, ['image_url', 'imageUrl']) ||
    getStringField(attrs, ['image_url', 'imageUrl'])
  if (!imageUrl) return null
  return {
    image_id:
      getStringField(response, ['image_id', 'imageId']) ||
      getStringField(attrs, ['image_id', 'imageId']),
    image_url: imageUrl,
  }
}

export function normalizePersonaMapKey(name: string): string {
  return name.trim().toLowerCase()
}

/** Map persona name keys to image URLs stamped on study response rows. */
export function buildResponseImageMap(responses: Array<any>): Map<string, string> {
  const imageByKey = new Map<string, string>()
  for (const response of responses) {
    const image = getResponseImage(response)
    if (!image?.image_url) continue
    for (const key of [
      getStringField(response, ['respondent_id', 'persona_name']),
      getStringField(response?.respondent_attributes, ['persona_name', 'name']),
    ]) {
      if (key) imageByKey.set(normalizePersonaMapKey(key), image.image_url)
    }
  }
  return imageByKey
}

/** The id keys a profile or response row may carry for one respondent. */
const RESPONDENT_ID_KEYS = ['respondent_id', 'respondentId', 'persona_id', 'personaId', 'persona_token']

/**
 * Map respondent ids to image URLs, from audience profiles or study response
 * rows (both carry `respondent_id`). The one speaker-image join for a stamped
 * quote-list (plots plan S): keyed by id only, never by a display name, since
 * two twins can share one.
 */
export function buildRespondentImageMapById(rows: Array<any>): Map<string, string> {
  const imageById = new Map<string, string>()
  for (const row of rows) {
    const image = getResponseImage(row)
    if (!image?.image_url) continue
    // Every id key a profile or response row may carry: a quote's respondentId can be any.
    for (const key of RESPONDENT_ID_KEYS) {
      const id = getStringField(row, [key])
      if (id && !imageById.has(id)) imageById.set(id, image.image_url)
    }
  }
  return imageById
}

export function mergeProfilesWithResponseImages(
  profiles: Array<RespondentProfile>,
  responses: Array<any>,
): Array<RespondentProfile> {
  const imageByKey = new Map<string, { image_id?: string; image_url?: string }>()
  for (const response of responses) {
    const image = getResponseImage(response)
    if (!image) continue
    for (const key of [
      getStringField(response, ['respondent_id', 'persona_name']),
      getStringField(response?.respondent_attributes, ['persona_name', 'name']),
    ]) {
      if (key) imageByKey.set(key, image)
    }
  }

  return profiles.map((profile) => {
    const image =
      imageByKey.get(profile.respondent_id) ||
      imageByKey.get(profile.name) ||
      imageByKey.get(String(profile.respondent_attributes?.persona_name || ''))
    if (!image?.image_url) return profile
    return {
      ...profile,
      image_id: image.image_id || profile.image_id,
      image_url: image.image_url,
      respondent_attributes: {
        ...profile.respondent_attributes,
        image_id: image.image_id || profile.image_id,
        image_url: image.image_url,
      },
    }
  })
}
